import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Textarea,
} from '@accure/ui';
import { Menu } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  ChevronDown,
  Clock,
  Edit,
  Loader2,
  MapPin,
  Trash2,
  Users,
} from 'lucide-react';
import { useBoolean } from 'usehooks-ts';
import { z } from 'zod';

import type { MeetingCommunication } from '@/api/fe-api/entities/communication';
import { CreateMeetingMinutes } from '@/api/fe-api/view/mutations/create-meeting-minutes';
import { DeleteMeeting } from '@/api/fe-api/view/mutations/delete-meeting';
import { DeleteMeetingMinutes } from '@/api/fe-api/view/mutations/delete-meeting-minutes';
import { UpdateMeetingMinutes } from '@/api/fe-api/view/mutations/update-meeting-minutes';
import { GetOneMeetingCommunication } from '@/api/fe-api/view/queries/get-one-meeting-communication';
import { formatDatetime, formatTime } from '@/utils/format/date';

import { useDetailsId } from '../hooks/use-details-id';

import { CommunicationDetailsBox } from './communication-details-box';
import { CommunicationDetailsHeader } from './communication-details-header';
import { CommunicationDetailsLinkedSites } from './communication-details-linked-sites';
import { CommunicationDetailsParticipant } from './communication-details-participant';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';

export type CommunicationDetailsMeetingProps = {
  id: string;
};

export function CommunicationDetailsMeeting({
  id,
}: CommunicationDetailsMeetingProps) {
  const [, setDetailsId] = useDetailsId();
  const meetingQuery = GetOneMeetingCommunication.useQuery({ id });
  const deleteMeeting = DeleteMeeting.useMutation();
  const showDeleteConfirm = useBoolean(false);
  const newMeetingMinutes = useBoolean(false);

  if (meetingQuery.isLoading) {
    return <LoadingSkeleton />;
  }

  if (meetingQuery.isError) {
    throw meetingQuery.error;
  }

  if (!meetingQuery.data) {
    throw new Error('No data');
  }

  const meeting = meetingQuery.data;

  async function onDelete() {
    await deleteMeeting.mutateAsync(meeting);
    showDeleteConfirm.setFalse();
    setDetailsId(undefined);
  }

  return (
    <>
      <Dialog
        open={showDeleteConfirm.value}
        onOpenChange={showDeleteConfirm.setValue}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="size-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete these meeting? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button
              variant="destructive"
              onClick={onDelete}
              type="button"
              disabled={deleteMeeting.isLoading}
            >
              {deleteMeeting.isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={showDeleteConfirm.setFalse}
              type="button"
              disabled={deleteMeeting.isLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl space-y-6 font-sans">
        <CommunicationDetailsHeader
          title={meeting.title}
          subtitle={meeting.start ? formatDatetime(meeting.start) : undefined}
          actions={
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none">
                <span>Actions</span>
                <div className="mx-1.5 h-3.5 w-px bg-gray-300"></div>
                <ChevronDown className="size-3.5" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={showDeleteConfirm.setTrue}
                        className={`${
                          active ? 'bg-gray-100 text-red-600' : 'text-red-600'
                        } flex w-full items-center px-4 py-2 text-sm`}
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete Meeting
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Menu>
          }
        />

        <CommunicationDetailsBox className="space-y-4">
          {meeting.start ? (
            <div className="flex items-center gap-4">
              <Clock className="size-5 text-gray-600" />
              <div>
                <p className="text-xs">Time</p>
                <p className="text-sm">
                  {formatTime(meeting.start)}{' '}
                  {meeting.end ? `- ${formatTime(meeting.end)}` : null}
                </p>
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-4">
            <MapPin className="text-gray-600" />
            <div>
              <p className="text-xs">Location</p>
              <p className="text-sm">{meeting.location || '-'}</p>
            </div>
          </div>

          {meeting.attendees && meeting.attendees.length > 0 ? (
            <div className="flex items-start gap-4">
              <Users className="size-5 text-gray-600" />
              <div className="flex-1 space-y-3">
                <div>
                  <div className="text-xs">Attendees</div>
                  <div className="space-y-1 text-sm">
                    {meeting.attendees.map((recipient, index) => (
                      <div key={index}>
                        <CommunicationDetailsParticipant
                          participant={recipient}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </CommunicationDetailsBox>

        <CommunicationDetailsBox>
          <div className="flex justify-between">
            <h3 className="mb-3 text-sm font-medium text-gray-800">
              Meeting Minutes
            </h3>

            {meeting.meetingMinutes.length === 0 && !newMeetingMinutes.value ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-primary flex h-7 items-center gap-1 px-2 text-xs"
                onClick={newMeetingMinutes.setTrue}
              >
                <Edit className="size-3.5" />
                Write meeting minutes
              </Button>
            ) : null}
          </div>

          <div className="space-y-4">
            {newMeetingMinutes.value ? (
              <MeetingMinutesNew
                meeting={meeting}
                onClose={newMeetingMinutes.setFalse}
              />
            ) : (
              meeting.meetingMinutes.map((meetingMinutes) => (
                <MeetingMinutes
                  key={meetingMinutes.id}
                  meeting={meeting}
                  meetingMinutes={meetingMinutes}
                />
              ))
            )}
          </div>
        </CommunicationDetailsBox>

        <CommunicationDetailsBox>
          <CommunicationDetailsLinkedSites communication={meeting} />
        </CommunicationDetailsBox>

        <CommunicationDetailsBox>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Summary</h3>
          <p className="overflow-x-auto whitespace-pre-wrap text-sm text-gray-600">
            {meeting.summary}
          </p>
        </CommunicationDetailsBox>
      </div>
    </>
  );
}

export type MeetingMinutesProps = {
  meeting: MeetingCommunication;
  meetingMinutes: MeetingCommunication['meetingMinutes'][number];
};

export function MeetingMinutes({
  meeting,
  meetingMinutes,
}: MeetingMinutesProps) {
  const editMode = useBoolean(false);

  if (editMode.value) {
    return (
      <MeetingMinutesEdit
        meeting={meeting}
        meetingMinutes={meetingMinutes}
        onClose={editMode.setFalse}
      />
    );
  }

  return (
    <div>
      <div className="relative mb-2 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={editMode.setTrue}
          className="absolute bottom-full right-0 h-7 px-2 text-xs"
        >
          <Edit className="mr-1 size-3.5" />
          Edit
        </Button>
      </div>
      <div className="prose prose-sm max-w-none overflow-x-auto whitespace-pre-wrap">
        {meetingMinutes.content}
      </div>
    </div>
  );
}

export type MeetingMinutesEditProps = {
  meeting: MeetingCommunication;
  meetingMinutes: MeetingCommunication['meetingMinutes'][number];
  onClose: () => void;
};

const MeetingMinutesEditFormSchema = z.object({
  content: z.string(),
});
type MeetingMinutesEditForm = z.infer<typeof MeetingMinutesEditFormSchema>;

export function MeetingMinutesEdit({
  meeting,
  meetingMinutes,
  onClose,
}: MeetingMinutesEditProps) {
  const updateMeetingMinutes = UpdateMeetingMinutes.useMutation();
  const deleteMeetingMinutes = DeleteMeetingMinutes.useMutation();

  const form = useForm<MeetingMinutesEditForm>({
    resolver: zodResolver(MeetingMinutesEditFormSchema),
    defaultValues: {
      content: meetingMinutes.content,
    },
  });

  const handleUpdate = useCallback(
    async function ({ content }: MeetingMinutesEditForm) {
      if (content.trim() === '') {
        await deleteMeetingMinutes.mutateAsync({
          id: meetingMinutes.id,
          meeting: { id: meeting.id },
        });
      } else {
        await updateMeetingMinutes.mutateAsync({
          id: meetingMinutes.id,
          content,
          title: meetingMinutes.title,
          meetingId: meeting.id,
        });
      }

      form.reset();

      onClose();
    },
    [
      onClose,
      form,
      meeting.id,
      meetingMinutes.title,
      meetingMinutes.id,
      updateMeetingMinutes,
      deleteMeetingMinutes,
    ],
  );

  const handleClose = useCallback(
    function () {
      onClose();
    },
    [onClose],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdate)} className="space-y-6">
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Enter meeting minutes content..."
                  className="min-h-[300px] text-sm"
                  disabled={updateMeetingMinutes.isLoading}
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row-reverse justify-start gap-2">
          <Button type="submit" disabled={updateMeetingMinutes.isLoading}>
            {updateMeetingMinutes.isLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Update'
            )}
          </Button>
          <Button
            className="bg-white"
            variant="outline"
            onClick={handleClose}
            type="button"
            disabled={updateMeetingMinutes.isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}

export type MeetingMinutesNewProps = {
  meeting: MeetingCommunication;
  onClose: () => void;
};

const MeetingMinutesNewFormSchema = z.object({
  content: z.string().min(1, 'The content is required'),
});
type MeetingMinutesNewForm = z.infer<typeof MeetingMinutesNewFormSchema>;

export function MeetingMinutesNew({
  meeting,
  onClose,
}: MeetingMinutesNewProps) {
  const createMeetingMinutes = CreateMeetingMinutes.useMutation();

  const form = useForm<MeetingMinutesNewForm>({
    resolver: zodResolver(MeetingMinutesNewFormSchema),
    defaultValues: {
      content: '',
    },
  });

  const handleCreate = useCallback(
    async function ({ content }: MeetingMinutesNewForm) {
      await createMeetingMinutes.mutateAsync({
        meetingId: meeting.id,
        content,
      });

      form.reset();

      onClose();
    },
    [onClose, form, createMeetingMinutes, meeting],
  );

  const handleClose = useCallback(
    function () {
      onClose();
    },
    [onClose],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreate)} className="space-y-6">
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Enter meeting minutes content..."
                  className="min-h-[300px] text-sm"
                  disabled={createMeetingMinutes.isLoading}
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row-reverse justify-start gap-2">
          <Button type="submit" disabled={createMeetingMinutes.isLoading}>
            {createMeetingMinutes.isLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Create'
            )}
          </Button>
          <Button
            className="bg-white"
            variant="outline"
            onClick={handleClose}
            type="button"
            disabled={createMeetingMinutes.isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}

function LoadingSkeleton() {
  return (
    <div className="max-w-7xl space-y-6">
      <div className="rounded-lg p-4">
        <Skeleton className="mb-2 h-8 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
      </div>

      <div className="space-y-4 rounded-lg border bg-card p-4">
        <div className="flex items-center">
          <Skeleton className="mr-2 size-5 rounded-full" />
          <div>
            <Skeleton className="mb-1 h-3 w-16" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>

        <div className="flex items-center">
          <Skeleton className="mr-2 size-5 rounded-full" />
          <div>
            <Skeleton className="mb-1 h-3 w-16" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>

        <div className="flex items-start">
          <Skeleton className="mr-2 size-5 rounded-full" />
          <div className="flex-1">
            <Skeleton className="mb-1 h-3 w-16" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-60" />
              <Skeleton className="h-4 w-52" />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <Skeleton className="mb-2 h-4 w-24" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <Skeleton className="mb-3 h-4 w-48" />
        <div className="mb-4 space-y-2">
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        <Skeleton className="h-8 w-40" />
      </div>
    </div>
  );
}
